import {
  CommentDto,
  CreateTradeTalkDto,
  EnhancePostRequest,
  EnhancePostResponse,
  FeedbackRequest,
  FeedbackResponse,
  GuardPostRequest,
  GuardPostResponse,
  TradeTalkBaseResponse,
  TradeTalkCategory,
  TradeTalkCommentResponse,
  TradeTalkResponse,
  TradeTalkTag,
  UpdateTradeTalkDto,
} from '@monorepo/client-types';
import type {
  NotificationName,
  NotificationResponse,
} from '@monorepo/notification-common';
import { PAGINATION_PARAMS } from '@hub/features/trade-talk/utils';
import BaseEntityService, { BaseResponse } from '../core/base-entity-service';

export default class TradeTalkService extends BaseEntityService {
  static override route = 'charts';

  async getAllTradeTalks(
    skip = PAGINATION_PARAMS.SKIP,
    limit = PAGINATION_PARAMS.LIMIT,
    search?: string,
    sortByOption?: string,
  ) {
    const allTradeTalks = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkResponse[]>
    >(`${this.path}/ideas`, {
      params: { skip, limit, text: search, sortByOption },
    });
    return allTradeTalks;
  }

  async getTradeTalk(id: string) {
    return this.httpService.get<TradeTalkResponse>(`${this.path}/ideas/${id}`);
  }

  async getTradeTalkComments(id: string, skip: number, limit: number) {
    return this.httpService.get<
      TradeTalkBaseResponse<TradeTalkCommentResponse[]>
    >(`${this.path}/ideas/${id}/comments`, { params: { skip, limit } });
  }

  async getPublicTradeTalkComments(id: string, skip: number, limit: number) {
    return this.httpService.get<
      TradeTalkBaseResponse<TradeTalkCommentResponse[]>
    >(`${this.path}/public/ideas/${id}/comments`, { params: { skip, limit } });
  }

  async createTradeTalk(newTradeTalk: CreateTradeTalkDto) {
    return this.httpService.post<
      CreateTradeTalkDto,
      TradeTalkResponse & { isFirstIdea?: boolean }
    >(`${this.path}/ideas`, newTradeTalk);
  }

  async updateTradeTalk(id: string, updatedTradeTalk: UpdateTradeTalkDto) {
    return this.httpService.patch<
      UpdateTradeTalkDto,
      TradeTalkResponse & { isFirstIdea?: boolean }
    >(`${this.path}/ideas/${id}`, updatedTradeTalk);
  }

  async deleteTradeTalk(id: string) {
    return this.httpService.delete<TradeTalkResponse>(
      `${this.path}/ideas/${id}`,
    );
  }

  async getCategories() {
    const allCategories = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkCategory[]>
    >(`${this.path}/idea-categories`, { params: { limit: 200 } });
    return allCategories.data;
  }

  async getTags() {
    const { data } = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkTag[]>
    >(`${this.path}/idea-tags`);
    return data;
  }

  async createTradeTalkNotification(notificationName: NotificationName) {
    return this.httpService.post<
      { notificationName: NotificationName },
      BaseResponse<NotificationResponse>
    >(`${this.path}/idea-notification`, {
      notificationName,
    });
  }

  async getAllPublicTradeTalks(
    skip = PAGINATION_PARAMS.SKIP,
    limit = PAGINATION_PARAMS.LIMIT,
    search?: string,
    authorId?: string,
    sortByOption?: string,
  ) {
    let url = `${this.path}/ideas/public`;
    if (authorId) {
      url += `/${authorId}`;
    }
    const publicTradeTalks = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkResponse[]>
    >(url, {
      params: { skip, limit, text: search, sortByOption },
    });
    return publicTradeTalks;
  }

  async get5ersPickTradeTalks(
    skip = PAGINATION_PARAMS.SKIP,
    limit = PAGINATION_PARAMS.LIMIT,
    search?: string,
    sortByOption?: string,
  ) {
    const picksTradeTalks = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkResponse[]>
    >(`${this.path}/ideas/public`, {
      params: {
        skip,
        limit,
        text: search,
        filter: { isPick: true },
        sortByOption,
      },
    });
    return picksTradeTalks;
  }

  async getPublicTradeTalksForGuests(
    skip = PAGINATION_PARAMS.SKIP,
    limit = PAGINATION_PARAMS.LIMIT,
    search?: string,
    sortByOption?: string,
  ) {
    const publicTradeTalks = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkResponse[]>
    >(`${this.path}/public/ideas`, {
      params: { skip, limit, text: search, sortByOption },
    });
    return publicTradeTalks;
  }

  async get5ersPickTradeTalksForGuests(
    skip = PAGINATION_PARAMS.SKIP,
    limit = PAGINATION_PARAMS.LIMIT,
    search?: string,
    sortByOption?: string,
  ) {
    const publicTradeTalks = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkResponse[]>
    >(`${this.path}/public/ideas`, {
      params: {
        skip,
        limit,
        text: search,
        filter: { isPick: true },
        sortByOption,
      },
    });
    return publicTradeTalks;
  }

  async getSimilarTradeTalks(id: string, limit: number) {
    return this.httpService.get<TradeTalkBaseResponse<TradeTalkResponse[]>>(
      `${this.path}/ideas/similar/${id}`,
      {
        params: { limit },
      },
    );
  }

  getSinglePublicTradeTalkForGuests(id: string) {
    return this.httpService.get<TradeTalkResponse>(
      `${this.path}/public/ideas/${id}`,
    );
  }

  async reportTradeTalk(id: string, reason: string) {
    return this.httpService.patch<{ reason: string }, TradeTalkResponse>(
      `${this.path}/ideas/${id}/report`,
      {
        reason,
      },
    );
  }

  async getAllPublicTradeTalksByUserPublicId(userPublicId: string) {
    const publicTradeTalks = await this.httpService.get<
      TradeTalkBaseResponse<TradeTalkResponse[]>
    >(`${this.path}/ideas/public/${userPublicId}`);
    return publicTradeTalks;
  }

  async updateTradeTalkLike(id: string) {
    return this.httpService.patch<{ reason: string }, TradeTalkResponse>(
      `${this.path}/ideas/${id}/like`,
    );
  }

  async createTradeTalkComment(commentDto: CommentDto) {
    const response = await this.httpService.post<
      CommentDto,
      TradeTalkCommentResponse
    >(`${this.path}/comments`, commentDto);

    return response;
  }

  async updateCommentLike(id: string) {
    return this.httpService.patch<object, TradeTalkCommentResponse>(
      `${this.path}/comments/${id}/like`,
    );
  }

  async updateCommentDislike(id: string) {
    return this.httpService.patch<object, TradeTalkCommentResponse>(
      `${this.path}/comments/${id}/dislike`,
    );
  }

  async getCommentReplies(id: string, skip: number, limit: number) {
    return this.httpService.get<
      TradeTalkBaseResponse<TradeTalkCommentResponse[]>
    >(`${this.path}/comments/${id}/replies`, { params: { skip, limit } });
  }

  async deleteComment(id: string) {
    return this.httpService.delete<TradeTalkCommentResponse>(
      `${this.path}/comments/${id}`,
    );
  }

  async updateCommentReport(id: string) {
    return this.httpService.patch<object, TradeTalkCommentResponse>(
      `${this.path}/comments/${id}/report`,
    );
  }

  async updateTradeTalkComment(id: string, content: string) {
    return this.httpService.patch<
      { content: string },
      TradeTalkCommentResponse
    >(`${this.path}/comments/${id}`, { content });
  }

  async analyzeTradeTalkPost(postData: {
    image_url: string;
    post_text: string;
    title: string;
  }) {
    return this.httpService.post<
      { image_url: string; post_text: string; title: string },
      unknown
    >('/trade-talk-ai/analyze-post', postData, { timeout: 0 });
  }

  async enhanceTradeTalkPost(postData: EnhancePostRequest) {
    return this.httpService.post<EnhancePostRequest, EnhancePostResponse>(
      '/trade-talk-ai/enhance-post',
      postData,
      { timeout: 0 },
    );
  }

  async guardTradeTalkPost(postData: GuardPostRequest) {
    return this.httpService.post<GuardPostRequest, GuardPostResponse>(
      '/trade-talk-ai/guard-post',
      postData,
      { timeout: 0 },
    );
  }

  async uploadChartImage(data: { thumbnail: string }) {
    return this.httpService.post<{ thumbnail: string }, { url: string }>(
      `${this.path}/upload-image`,
      data,
    );
  }

  async getTradeTalkLikes(id: string, skip = 0, limit = 20) {
    return this.httpService.get<
      TradeTalkBaseResponse<{
        [userId: string]: {
          userId: string;
          createdAt: string;
          user: {
            author: string;
            userImg: string;
            email: string;
          };
        };
      }>
    >(`${this.path}/ideas/${id}/likes`, { params: { skip, limit } });
  }

  async submitFeedback(feedback: FeedbackRequest) {
    return this.httpService.post<FeedbackRequest, FeedbackResponse>(
      `${this.path}/feedbacks`,
      feedback,
    );
  }
}
